
import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import Notification from '@/mixins/Notification';
import VWrapper from '@/components/shared/VWrapper.vue';
import { namespace } from 'vuex-class';
import { Venue } from '@/interfaces/models/Venue';
import OptionTable from '@/components/option/OptionTable.vue';
import { OptionGroup } from '@/interfaces/models/OptionGroup';
import VEmptyState from '@/components/shared/VEmptyState.vue';
import { Page } from '@/interfaces/api/Page';
import Filter from '@/interfaces/api/Filter';
import OptionArticleSimpleTable from '@/components/option/article/OptionArticleSimpleTable.vue';
import { OptionArticle } from '@/interfaces/models/OptionArticle';

const venue = namespace('venue');
const foodcard = namespace('foodcard');

@Component({
  components: { VEmptyState, OptionTable, VWrapper, OptionArticleSimpleTable },
})
export default class Category extends mixins(Notification) {
  @venue.State('active') public venue!: Venue;

  @foodcard.State('optionFilter') public optionFilter!: Filter;
  @foodcard.State((state) => state.optionPagination.total) public total!: number;

  @foodcard.Action('setOptionFilter') public setOptionFilter!: any;
  @foodcard.Action('makeOptionVisible') public makeOptionVisible!: any;
  @foodcard.Action('hideOption') public hideOption!: any;
  @foodcard.Action('copyOption') public copy!: any;
  @foodcard.Action('fetchPaginatedOptions') public getOptions!: any;
  @foodcard.Action('destroyOption') public deleteOption!: any;
  @foodcard.Action('destroyOptions') public deleteOptions!: (data: { ids: string[] }) => {};
  @foodcard.State('options') public items!: OptionGroup[];
  @foodcard.Action('setActiveOption') public setActiveOption!: any;
  @foodcard.Action('resetOptions') public resetOptions!: any;
  @foodcard.Action('deactivateOptionArticle') public deactivateOptionArticle!: any;
  @foodcard.Action('activateOptionArticle') public activateOptionArticle!: any;
  @foodcard.Action('makeArticleOptionVisible') public makeArticleOptionVisible!: any;
  @foodcard.Action('hideOptionArticle') public hideOptionArticle!: any;
  @foodcard.Action('updateOption') public updateOption!: any;

  public showEditOptionArticleModal: boolean = false;

  public optionGroup: OptionGroup | null = null;

  public created() {
    this.resetOptions();
    this.setOptionFilter({});
    if (!this.venue) {
      this.$startLoading('venue');
    }
  }

  public async destroy(opt: OptionGroup) {
    this.$startLoading('option');
    await this.deleteOption({ id: opt._id });
    this.$stopLoading('option');
  }

  public async destroyGroup(optIds: string[]) {
    this.$startLoading('options');
    await this.deleteOptions({ ids: optIds });
    await this.getOptions();
    this.$stopLoading('options');
  }

  public async toggleOptionVisibility({ item, value }: { item: OptionGroup; value: boolean }) {
    this.$startLoading('option.article');
    if (value) {
      this.hideOption({ venue: this.venue._id, options: [item._id] });
    } else {
      this.makeOptionVisible({ venue: this.venue._id, options: [item._id] });
    }
    this.$stopLoading('option.article');
  }

  public async toggleOptionMultiple({ item, value }: { item: OptionGroup; value: boolean }) {
    this.$startLoading('option.article');
    await this.updateOption({ ...item, id: item._id, hasMultiple: !value });
    this.$stopLoading('option.article');
  }

  public async toggleVisibility({ item, value }: { item: OptionArticle; value: boolean }) {
    this.$startLoading('option.article');
    if (value) {
      this.hideOptionArticle({ venue: this.venue._id, articles: [item._id] });
    } else {
      this.makeArticleOptionVisible({ venue: this.venue._id, articles: [item._id] });
    }
    this.$stopLoading('option.article');
  }

  public async toggleActivation({ item, value }: { item: OptionArticle; value: boolean }) {
    this.$startLoading('option.article');
    if (value) {
      this.deactivateOptionArticle({ venue: this.venue._id, articles: [item._id] });
    } else {
      this.activateOptionArticle({ venue: this.venue._id, articles: [item._id] });
    }
    this.$stopLoading('option.article');
  }

  public async onSearch(searchOptions: { search: string; filterOptions: boolean }) {
    this.setOptionFilter({ ...this.optionFilter, ...searchOptions });
    this.getOptions();
  }

  public async onCopy(id: string) {
    const page = this.total > 0 ? Math.ceil((this.total + 1) / this.optionFilter.limit!) : 1;

    try {
      this.$startLoading('option.copy');
      await this.copy({ id });
      this.notifySuccess('venue.notification.copyOptionSuccess');
    } catch (error) {
      console.log(error);
      this.notifyError('venue.notification.copyOptionError');
    } finally {
      this.$stopLoading('option.copy');
      this.getOptions(page);
    }
  }

  public setOptionSearchFilter() {
    if (!this.$route.query) return;

    if (this.$route.query.search) {
      this.setOptionFilter({
        ...this.optionFilter,
        search: String(this.$route.query.search),
      });
    }

    if (this.$route.query.filterOptions) {
      this.setOptionFilter({
        ...this.optionFilter,
        filterOptions: this.$route.query.filterOptions,
      });
    }
  }

  public async onPageChange(page: Page) {
    if (!this.venue?._id) return;
    if (page.limit) {
      this.setOptionFilter({ ...this.optionFilter, ...{ venue: this.venue._id, page: page.page, limit: page.limit } });
    }
    this.setOptionSearchFilter();
    this.$startLoading('option');
    await this.getOptions(page.page);
    this.$stopLoading('option');
  }

  public async updateProp(data: { original: OptionArticle; field: string; value: string }) {
    if (data.field === 'price') {
      return;
    }

    const update: any = {};
    update[data.field] = data.value;

    this.updateOption({ id: data.original._id, ...data.original, ...update });
  }

  public async mounted() {
    if (!this.venue) {
      this.$startLoading('venue');
    }
  }

  public handleOptionGroupCell(event: OptionGroup) {
    this.setActiveOption(event);
    this.optionGroup = event;
    this.showEditOptionArticleModal = true;
  }

  public async handleCancelOptionArticle() {
    this.showEditOptionArticleModal = false;
    this.optionGroup = null;
  }

  @Watch('venue')
  public async onVenueChange() {
    if (this.venue && this.venue._id) {
      if (!this.$isLoading('option')) {
        this.setOptionFilter({ venue: this.venue._id, limit: 20 });
        this.setOptionSearchFilter();
        this.$startLoading('option');
        await this.getOptions(1);
        this.$stopLoading('option');
      }
    }
  }
}
